import clsx from 'clsx'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React from 'react'

import { ampli } from '@Analytics/ampli'
import { NativeModalBody, NativeModalFooter, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { Button } from '@CoreComponents/Buttons/Button'
import { SuccessPromptTactics } from '@Domains/Cart/Components/Prompt/Success/types'
import { SideCartContext } from '@Domains/Cart/Context/SideCartContextProvider'
import { LineItem } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/LineItem/LineItem'
import { FreeShippingCountdown } from '@Domains/Cart/Feature/FreeShippingCountdown/FreeShippingCountdown'
import { CartMilestonesV2 } from '@Domains/Cart/Feature/SideCart/Components/CartMilestones/CartMilestonesV2'
import AddToCartPromptSuccessTactics from '@Domains/Product/Features/Tactics/AddToCartSuccessPromptTactics'
import { AddToCartProductInput, useCartQuery } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'
import { giftWrappingIDs } from '@Shared/Statics/GiftWrappings'

import styles from './SuccessPrompt.module.scss'

const GIFT_ID = '26342'

export const SuccessPrompt = ({
  onClose,
  onModalToggle,
  origins,
  primaryLineItemID,
  tactics,
}: {
  onClose: () => void
  // eslint-disable-next-line react/no-unused-prop-types
  onModalWithin?: (open: boolean) => void
  // eslint-disable-next-line @typescript-eslint/member-ordering
  onModalToggle?: (open: boolean) => void
  origins?: null | AddToCartProductInput[]
  primaryLineItemID?: string
  tactics?: SuccessPromptTactics | null
}) => {
  const { data } = useCartQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  // const [matchingVariantProductSuccess, setMatchingVariantProductSuccess] = React.useState<boolean>(false)
  // const campaignOverrideV1 = useSplitTest('campaign_override_v1', 'b')
  // const [addedIds, setAddedIds] = React.useState<string[]>([])
  const { t } = useTranslation()

  const [lineItemGroup, lineItemsInGroup] = React.useMemo(() => {
    if (primaryLineItemID) {
      const group = data?.cart?.lineItemGroups?.find(g => g.lineItemIDs?.includes(primaryLineItemID))
      if (!group) {
        return [null, null]
      }
      return [group, data?.cart?.lineItems?.filter(lineItem => group.lineItemIDs?.includes(lineItem.id))]
    }
    return [null, null]
  }, [data?.cart?.lineItemGroups, data?.cart?.lineItems, primaryLineItemID])

  React.useEffect(() => {
    ampli.promptOpen({ source_id: 'success_prompt' })
  }, [])

  const lineItems = React.useMemo(() => data?.cart?.lineItems || [], [data?.cart?.lineItems])
  const lineItem = React.useMemo(() => {
    const firstOrigin = first(origins)
    // IF GIFT_CARD GET THE ONE WITH SELECTED AMOUNT
    if (firstOrigin && firstOrigin.productID === GIFT_ID && firstOrigin?.giftcard?.amount) {
      const giftAmount = firstOrigin.giftcard.amount
      return lineItems?.find(_lineItem => _lineItem.productID === GIFT_ID && `${_lineItem.totalPrice?.plain || 0}`.replace(/^\D+/g, '') === `${giftAmount}`)
            || lineItems?.find(_lineItem => _lineItem.id === primaryLineItemID)
    }
    return lineItems?.find(_lineItem => _lineItem.id === primaryLineItemID)
  }, [origins, lineItems, primaryLineItemID])
  const personalizationsLinkedToLineItem = React.useMemo(() => lineItems?.filter(
    l => l.type?.__typename === 'CartLineItemTypeUpsellPersonalization' && l.type?.parent === lineItem?.type?.link,
  ), [lineItem?.type?.link, lineItems])
  const { openCart } = React.useContext(SideCartContext)

  const handleOpenCart = () => {
    openCart()
    onClose()
  }

  const haveMilestones = React.useMemo(() => [
    !isEmpty(data?.cart?.milestones?.steps),
    data?.cart?.milestones?.steps?.length === 1 && first(data?.cart?.milestones?.steps)?.type === 'FREE_SHIPPING',
  ], [data?.cart?.milestones?.steps])

  return (
    <>
      <NativeModalHeader className={styles.modalHeader}>
        {t('cart:ADDED_TO_CART')}
      </NativeModalHeader>
      <NativeModalBody className={clsx(styles.modalBody)}>

        {lineItemGroup && (
          <>
            <div className={styles.groupHeader}>
              <div className={styles.title}>{t('cart:BUNDLE_DISCOUNT_LABEL')}</div>
              <div className={styles.prices}>
                {lineItemGroup.totalPrice?.discount?.format && <span className={clsx(styles.discount)}>{lineItemGroup.totalPrice?.discount?.format}</span>}
                <span className={clsx(styles.price, lineItemGroup.totalPrice?.discount?.format && styles.discounted)}>{lineItemGroup.totalPrice?.format}</span>
              </div>
            </div>
            <div className={styles.lineItemGroup}>
              {lineItemsInGroup && map(lineItemsInGroup, groupedLineItem => (
                <LineItem
                  key={`${lineItemGroup.id}_${groupedLineItem.id}`}
                  className={styles.lineItemInGroup}
                  giftWrappingIsAssociated={!!origins?.find(o => giftWrappingIDs.includes(o.productID))}
                  groupItem
                  isPowerStep
                  lineItem={groupedLineItem}
                  onModalToggle={onModalToggle}
                  showPersonalizationModifiers={false}
                  showPersonalizeImagesOnAddedProducts={false}
                  showPersonalizeImagesOnUnaddedProducts={false}
                  showQuantity={false}
                  showQuantityBubble={false}
                  showQuantityButtons={false}
                  showUpsell={false}
                />
              ))}
            </div>
          </>
        )}

        {!lineItemsInGroup && lineItem && (
          <div className={styles.lineItem}>
            <LineItem
              giftWrappingIsAssociated={!!origins?.find(o => giftWrappingIDs.includes(o.productID))}
              isPowerStep
              lineItem={lineItem}
              onModalToggle={onModalToggle}
              personalizationsLinkedToLineItem={personalizationsLinkedToLineItem}
              showPersonalizationModifiers={false}
              showPersonalizeImagesOnAddedProducts={false}
              showPersonalizeImagesOnUnaddedProducts={false}
              showQuantity={false}
              showQuantityButtons={false}
              showUpsell={false}
            />
          </div>
        )}

        {!haveMilestones && (
          <FreeShippingCountdown
            expectedDelivery={data?.cart?.expectedDelivery || ''}
            isFree={!!data?.cart?.freeShipping?.isFree}
            remaining={data?.cart?.freeShipping?.remaining || undefined}
            showFreeShippingProgress
            v2
          />
        )}
        {haveMilestones && <CartMilestonesV2 cart={data?.cart} className="fullwidth" />}

        <AddToCartPromptSuccessTactics lineItem={lineItem} tactics={tactics?.gold} />
        <AddToCartPromptSuccessTactics lineItem={lineItem} tactics={tactics?.silver} />
      </NativeModalBody>
      <NativeModalFooter className={styles.modalFooter}>
        <Button className={styles.button} innerSpacing="medium" onClick={handleOpenCart} theme="black">
          {t('GO_TO_CART')}
        </Button>
        <Button
          className={clsx(styles.button, styles.continueButton)}
          innerSpacing="medium"
          onClick={() => onClose()}
          theme="white"
          type="button"
        >
          {t('cart:PP_CONTINUE_SHOPPING_BUTTON_TEXT')}
        </Button>
      </NativeModalFooter>
    </>
  )
}
